/**
 * @fileoverview Generic KPI card row.
 * @description A row of icon-tile KPI cards, each showing a tinted icon, an uppercase label, a
 * large value, and an optional one-line sublabel. Originally built for the sales module (every one
 * of its pages opens with this exact shape, just different labels/values) but has no
 * sales-specific coupling, so it lives here as a shared molecule any page can reuse rather than
 * being duplicated or re-exported through a single feature's barrel. The row is one column on
 * phones, two on small screens, and four across on desktop (five when a page passes five tiles).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import Icon from '@/shared/components/atoms/Icon'

import type { IconName } from '@/shared/components/atoms/Icon'

export type KpiIconTone = 'navy' | 'blue' | 'purple' | 'amber' | 'green' | 'red'

interface KpiTile {
  label: string
  value: string | number
  sublabel?: string | undefined
  icon: IconName
  iconTone: KpiIconTone
  /** Colours the value when it needs attention: a warning, a danger or a success. */
  tone?: 'success' | 'warning' | 'danger' | undefined
  testId?: string | undefined
}

interface KpiStripProps {
  tiles: KpiTile[]
}

/** Tile background + icon stroke per tone — each pair reads as one tinted badge. */
const ICON_TONE_CLASS_NAMES: Record<KpiIconTone, string> = {
  navy: 'bg-info-soft-bg text-accent-panel',
  blue: 'bg-info-bg text-primary',
  purple: 'bg-purple-bg text-purple',
  amber: 'bg-status-warning-bg text-status-warning',
  green: 'bg-success-bg text-teal',
  red: 'bg-danger-bg text-danger',
}

/** The value's colour when a tile carries a tone; without one the value stays primary. */
const VALUE_TONE_CLASS_NAMES: Record<NonNullable<KpiTile['tone']>, string> = {
  success: 'text-success',
  warning: 'text-status-warning',
  danger: 'text-danger',
}

const GRID_COLUMNS_CLASS_NAMES: Record<'default' | 'five', string> = {
  default: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  five: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
}

/**
 * @component
 * @description Renders the page's KPI cards in a responsive grid.
 * @param {KpiStripProps} props - The tiles to render.
 * @param {KpiTile[]} props.tiles - The label/value/icon/sublabel for each card, in display order.
 * A tile's optional `tone` colours its value (success, warning or danger) so a figure that needs
 * attention stands out from a calm one. Each tile's optional `testId` goes on its card, with "-label" and "-value" appended for the two
 * texts inside it, so QA can find a card and read it (e.g. active-clients-kpi-total-value).
 */
function KpiStrip({ tiles }: KpiStripProps) {
  const columns = tiles.length === 5 ? 'five' : 'default'

  return (
    <section
      className={`grid gap-3.5 ${GRID_COLUMNS_CLASS_NAMES[columns]}`}
      data-testid="kpi-strip"
    >
      {tiles.map((tile) => (
        <div
          key={tile.label}
          data-testid={tile.testId}
          className="flex min-w-0 items-center gap-3.5 rounded-xl border border-chip-border bg-white px-4.5 py-4"
        >
          <span
            className={`inline-flex size-kpi-icon shrink-0 items-center justify-center rounded-xl ${ICON_TONE_CLASS_NAMES[tile.iconTone]}`}
          >
            <Icon name={tile.icon} size={22} />
          </span>
          <div className="flex min-w-0 flex-col leading-tight">
            <span
              data-testid={tile.testId ? `${tile.testId}-label` : undefined}
              className="truncate text-micro font-semibold tracking-label text-kpi-muted"
            >
              {tile.label}
            </span>
            <span
              data-testid={tile.testId ? `${tile.testId}-value` : undefined}
              className={`mt-0.75 mb-0.5 text-kpi font-bold tracking-kpi ${tile.tone ? VALUE_TONE_CLASS_NAMES[tile.tone] : 'text-primary'}`}
            >
              {tile.value}
            </span>
            {tile.sublabel && (
              <span
                data-testid={tile.testId ? `${tile.testId}-sublabel` : undefined}
                className="truncate text-xs text-kpi-muted"
              >
                {tile.sublabel}
              </span>
            )}
          </div>
        </div>
      ))}
    </section>
  )
}

export default KpiStrip
